import { useEffect } from 'react';

import { FormModal } from '@/components/modals/FormModal/FormModal';
import { FormModalActions } from '@/components/modals/FormModal/FormModalActions';
import {
  FeedbackMessages,
  useFeedback,
} from '@/components/notifications/Feedback/useFeedback';
import { LoadingOverlay } from '@/components/progress/LoadingOverlay/LoadingOverlay';
import {
  FormProvider,
  useForm,
  useFormContext,
} from '@/components/react-hook-form';
import { useFormSaveHandler } from '@/hooks/useFormSaveHandler';
import { useReportError } from '@/hooks/useReportError';
import { HouseholdPermissionAwareDisabledProvider } from '@/modules/authentication/context/HouseholdPermissionAwareDisabledProvider/HouseholdPermissionAwareDisabledProvider';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import {
  AugmentedUpdateDsuePortabilityInput,
  CreateDsuePortabilityInput,
  UpdateDsuePortabilityInput,
} from '@/types/schema';

import {
  DSUEFormShape,
  DSUEFormShapePaths,
  initialFormValues,
} from './DSUEModal.constants';
import { DSUEModalForm } from './DSUEModalForm';
import {
  DsueFormClientFragment,
  useCreateDsuePortabilityMutation,
  useDeleteDsuePortabilityMutation,
  useUpdateDsuePortabilityMutation,
} from './graphql/DSUEForm.generated';
import { useDSUEData } from './hooks/useDSUEData';

interface DSUEModalInnerProps {
  variant: 'create' | 'edit';
  onClose: () => void;
  heading: string;
  showLoadingOverlay: boolean;
  clientData: DsueFormClientFragment[];
  householdId: string;
}

function DSUEModalInner({
  variant,
  onClose,
  heading,
  showLoadingOverlay,
  clientData,
  householdId,
}: DSUEModalInnerProps) {
  const { formRef, handleSave } = useFormSaveHandler();
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const { handleSubmit, reset, shouldBlockNavigation, formState, getValues } =
    useFormContext<DSUEFormShape>();
  const canEdit = useCanEditHouseholdData();

  const closeModal = () => {
    reset();
    onClose();
  };

  const [saveDsue] = useCreateDsuePortabilityMutation({
    refetchQueries: 'active',
    awaitRefetchQueries: true,
    onError: (error) => {
      showFeedback(FeedbackMessages.formSaveError);
      reportError(`Could not create client DSUE`, error);
    },
    onCompleted: () => {
      showFeedback(FeedbackMessages.formSaveSuccess, { variant: 'success' });
      closeModal();
    },
  });

  const [updateDsue] = useUpdateDsuePortabilityMutation({
    refetchQueries: 'active',
    awaitRefetchQueries: true,
    onError: (error) => {
      showFeedback(FeedbackMessages.formSaveError);
      reportError(`Could not update client DSUE`, error);
    },
    onCompleted: () => {
      showFeedback(FeedbackMessages.formSaveSuccess, { variant: 'success' });
      closeModal();
    },
  });

  const [handleDelete, { loading: isDeleting }] =
    useDeleteDsuePortabilityMutation({
      refetchQueries: 'active',
      awaitRefetchQueries: true,
      onError: (error) => {
        showFeedback(FeedbackMessages.deleteError);
        reportError(`Could not delete client DSUE`, error);
      },
      onCompleted: () => {
        showFeedback(FeedbackMessages.formSaveSuccess, { variant: 'success' });
        closeModal();
      },
    });

  const onSubmit = handleSubmit((data) => {
    if (!data.id) {
      const createInput: CreateDsuePortabilityInput = {
        householdID: householdId,
        recipientID: data.primaryClientId,
        amount: data.amount,
        eventDate: data.asOfDate,
        notes: data.notes,
        documentIDs: data.documents,
      };

      return saveDsue({
        variables: {
          input: {
            create: createInput,
          },
        },
      });
    } else {
      // Note, documents are associated with the DSUE by the document uploader
      const updateInput: UpdateDsuePortabilityInput = {
        recipientID: data.primaryClientId,
        amount: data.amount,
        eventDate: data.asOfDate,
        notes: data.notes,
        clearDocuments: false,
      };

      const input: AugmentedUpdateDsuePortabilityInput = {
        id: data.id,
        update: updateInput,
      };

      return updateDsue({
        variables: {
          input,
        },
      });
    }
  });

  return (
    <FormModal
      isOpen={true}
      onClose={onClose}
      heading={heading}
      shouldBlockClose={shouldBlockNavigation}
      actions={
        <FormModalActions.Provider
          formState={formState}
          isDeleting={isDeleting}
          viewOnly={!canEdit}
        >
          {variant === 'edit' && (
            <FormModalActions.DeleteButton
              onConfirmDelete={() => {
                const id = getValues(
                  'id' as const satisfies DSUEFormShapePaths
                );
                if (id) {
                  void handleDelete({
                    variables: {
                      id,
                    },
                  });
                }
              }}
            />
          )}
          <FormModalActions.CancelButton onClick={closeModal} />
          <FormModalActions.SaveButton onClick={handleSave}>
            {variant === 'create' ? 'Log DSUE amount' : 'Save changes'}
          </FormModalActions.SaveButton>
        </FormModalActions.Provider>
      }
    >
      <HouseholdPermissionAwareDisabledProvider
        forceDisabled={showLoadingOverlay}
      >
        <form ref={formRef} onSubmit={onSubmit} noValidate>
          <LoadingOverlay open={showLoadingOverlay} />
          <DSUEModalForm clientData={clientData} householdId={householdId} />
        </form>
      </HouseholdPermissionAwareDisabledProvider>
    </FormModal>
  );
}

export interface DSUEModalProps {
  householdId: string;
  primaryClientId: string;
  variant: 'create' | 'edit';
  onClose: () => void;
}

export function DSUEModal({
  householdId,
  primaryClientId,
  variant,
  onClose,
}: DSUEModalProps) {
  const formMethods = useForm<DSUEFormShape>({
    defaultValues: initialFormValues,
  });

  const { clientData, loading } = useDSUEData(householdId);

  // If the data is loading
  const showLoadingOverlay = loading;

  const { reset, formState } = formMethods;

  useEffect(() => {
    // Avoid clobbering user changes while they are typing or submitting
    if (formState.isDirty || formState.isSubmitting) {
      return;
    }

    const initialClient = clientData?.find(
      (client) => client.id === primaryClientId
    );

    const dsueId = initialClient?.dsue?.id;

    const initialDocuments =
      clientData?.flatMap(
        (client) =>
          client.dsue?.documents?.flatMap((doc) => {
            if (client.dsue?.id === dsueId) {
              return doc.id;
            }
            return [];
          }) ?? []
      ) ?? [];

    if (!initialClient) {
      return;
    }

    reset({
      id: dsueId ?? undefined,
      primaryClientId: initialClient.id,
      amount: initialClient?.dsue?.amount,
      asOfDate: initialClient?.dsue?.eventDate,
      notes: initialClient?.dsue?.notes ?? '',
      documents: initialDocuments,
    });
  }, [
    clientData,
    formState.isDirty,
    formState.isSubmitting,
    primaryClientId,
    reset,
  ]);

  const heading = variant === 'create' ? 'Log DSUE amount' : 'Edit DSUE amount';

  return (
    <FormProvider {...formMethods}>
      <DSUEModalInner
        variant={variant}
        onClose={onClose}
        heading={heading}
        showLoadingOverlay={showLoadingOverlay}
        clientData={clientData ?? []}
        householdId={householdId}
      />
    </FormProvider>
  );
}
